import React, { useEffect, useRef, useState } from "react";
import { Loader2, Upload, Trash2, Images, Check } from "lucide-react";
import { api, resolveImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function MediaManager() {
  const { authHeaders, canEdit } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState("");
  const fileRef = useRef();

  const load = async () => { const { data } = await api.get("/admin/media", { headers: authHeaders() }); setFiles(data); setLoading(false); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 2500); };

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/admin/upload", fd, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } });
      flash("Image ajoutée"); load();
    } catch { flash("Échec de l'upload"); } finally { setUploading(false); }
  };

  const del = async (f) => {
    if (!window.confirm("Supprimer cette image ?")) return;
    try { await api.delete(`/admin/media/${f.id}`, { headers: authHeaders() }); flash("Image supprimée"); load(); }
    catch (e) { flash(e.response?.data?.detail || "Suppression impossible"); }
  };

  if (loading) return <div className="flex h-40 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-caneat-orange" /></div>;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2"><Images className="h-6 w-6 text-caneat-orange" /><h1 className="caneat-title text-3xl text-caneat-ink">Médias</h1></div>
        {canEdit && (
          <>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="media-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="media-upload-btn"
              className="flex items-center gap-2 rounded-full bg-caneat-orange px-4 py-2.5 text-sm font-bold text-white shadow-lg">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Uploader</button>
          </>
        )}
      </div>
      <p className="mb-4 text-sm font-medium text-caneat-muted">Images optimisées automatiquement pour le web. Une image utilisée par un produit ne peut pas être supprimée.</p>
      {msg && <div className="mb-3 rounded-xl bg-emerald-100 px-4 py-2 text-sm font-bold text-emerald-700">{msg}</div>}

      {files.length === 0 ? (
        <p className="mt-10 text-center text-sm text-caneat-muted">Aucune image pour le moment.</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {files.map((f) => (
            <div key={f.id} className="group relative overflow-hidden rounded-2xl bg-white ring-1 ring-caneat-cream" data-testid={`media-${f.id}`}>
              <div className="flex h-32 items-center justify-center bg-caneat-pageCream"><img src={resolveImage(f.url)} alt={f.original_filename} className="h-full w-full object-contain p-2" /></div>
              <div className="flex items-center justify-between px-2 py-1.5">
                {f.in_use ? <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-600"><Check className="h-3 w-3" /> utilisée</span> : <span className="text-[10px] font-semibold text-caneat-muted">non utilisée</span>}
                {canEdit && !f.in_use && <button onClick={() => del(f)} className="flex h-7 w-7 items-center justify-center rounded-lg bg-red-50 text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
